import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, Upload, Database, FlaskConical, FileText, Sheet, FileSignature } from "lucide-react";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";

// New study build: upload new files (protocol + budget + optional CTA) or pick
// documents already in the Data Lake — mixing both is allowed. Posts one
// multipart request to the async job endpoint; the page polls afterwards.
export default function StudyBuildModal({ qaMode = false, defaultClientId = "", onClose, onDone }) {
    const [mode, setMode] = useState("upload");
    const [clientId, setClientId] = useState(defaultClientId);
    const [files, setFiles] = useState({ protocol: null, budget: null, cta: null });
    const [ids, setIds] = useState({ protocol: "", budget: "", cta: "" });
    const [siteId, setSiteId] = useState("");
    const [matching, setMatching] = useState("ai");
    const [busy, setBusy] = useState(false);

    const hasProtocol = Boolean(files.protocol || ids.protocol);
    const hasBudget = Boolean(files.budget || ids.budget);

    const submit = async () => {
        if (!clientId || !hasProtocol || !hasBudget) { toast.error("Pick the client, a protocol and a budget"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("client_id", clientId);
            ["protocol", "budget", "cta"].forEach((k) => {
                if (files[k]) fd.append(`${k}_file`, files[k]);
                else if (ids[k]) fd.append(`${k}_id`, ids[k]);
            });
            if (siteId) fd.append("site_id", siteId);
            fd.append("matching", matching);
            fd.append("qa_mode", qaMode ? "true" : "false");
            const { data } = await api.post(`/admin/study-assets/study-builds/jobs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Study build started — extraction and composition run in the background");
            onDone?.(data);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-2xl p-6 max-h-[92vh] overflow-auto" onMouseDown={(e) => e.stopPropagation()} data-testid="study-build-modal">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h2 className="font-display text-2xl">New Study Build</h2>
                        <p className="text-xs text-white/50 mt-1">Protocol + budget (+ CTA) → visit schedule with pricing and every protocol-vs-budget disagreement.</p>
                    </div>
                    <button onClick={onClose} data-testid="sb-close"><X className="w-5 h-5 text-white/50" /></button>
                </div>
                {qaMode && (
                    <div className="p-2.5 rounded-md border border-[#7C3AED]/30 bg-[#7C3AED]/5 text-xs text-white/70 flex items-center gap-2 mb-3" data-testid="sb-qa-banner">
                        <FlaskConical className="w-3.5 h-3.5 text-[#c7a6ff]" /> QA mode: builds are filed under the selected client; the shared <code className="text-[#c7a6ff] mx-1">_training</code> test protocols are selectable in “existing documents”.
                    </div>
                )}
                <div className="flex gap-1 mb-4 rounded-full border border-white/10 p-1 w-fit" data-testid="sb-mode-tabs">
                    {[["upload", "Upload new files", Upload], ["existing", "Use existing documents", Database]].map(([k, label, Icon]) => (
                        <button key={k} onClick={() => setMode(k)} data-testid={`sb-mode-${k}`}
                            className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 ${mode === k ? "bg-[#7C3AED] text-white" : "text-white/60 hover:text-white"}`}>
                            <Icon className="w-3.5 h-3.5" /> {label}
                        </button>
                    ))}
                </div>
                <div className="space-y-3">
                    <ClientPicker value={clientId} onChange={(v) => { setClientId(v); setIds({ protocol: "", budget: "", cta: "" }); }} label="1 · Client" required excludeDisabled includeSuperdomain={qaMode} testId="sb-client-id" />
                    {mode === "upload"
                        ? <UploadFields files={files} setFiles={setFiles} ids={ids} setIds={setIds} />
                        : <ExistingPicker qaMode={qaMode} clientId={clientId} ids={ids} setIds={setIds} files={files} />}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <div className="text-xs text-white/50 mb-1">Site id (for newly ingested documents)</div>
                            <input value={siteId} onChange={(e) => setSiteId(e.target.value)} placeholder="e.g. 207" data-testid="sb-site-id"
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                        </label>
                        <label className="block">
                            <div className="text-xs text-white/50 mb-1">Procedure matching</div>
                            <select value={matching} onChange={(e) => setMatching(e.target.value)} data-testid="sb-matching" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
                                <option value="ai">AI-assisted (Claude) with rule fallback</option>
                                <option value="rules">Rules only (no LLM)</option>
                            </select>
                        </label>
                    </div>
                </div>
                <p className="text-xs text-white/40 mt-3">New files are ingested first (protocol extraction, budget AI agent, CTA clauses — a few minutes for a long protocol); the build then follows the protocol’s schedule of events and lists each disagreement with the budget.</p>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-white/60">Cancel</button>
                    <button onClick={submit} disabled={busy || !clientId || !hasProtocol || !hasBudget} data-testid="sb-submit-btn"
                        className="px-4 py-2 rounded-full bg-[#7C3AED] text-white text-sm disabled:opacity-50">
                        {busy ? "Starting…" : "Start build"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const KINDS = [
    { k: "protocol", label: "Protocol", accept: ".pdf,.docx,.doc,.txt", Icon: FileText, required: true },
    { k: "budget", label: "Budget / payment schedule", accept: ".xlsx,.xlsm,.pdf", Icon: Sheet, required: true },
    { k: "cta", label: "CTA (optional)", accept: ".docx,.doc,.pdf,.txt", Icon: FileSignature, required: false },
];

function UploadFields({ files, setFiles, ids, setIds }) {
    return (
        <div className="space-y-2" data-testid="sb-upload-fields">
            {KINDS.map(({ k, label, accept, Icon, required }) => (
                <div key={k} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-white/60 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-[#c7a6ff]" /> {label}{required ? " *" : ""}</div>
                        {files[k] && <button onClick={() => setFiles({ ...files, [k]: null })} className="text-[11px] text-white/40 hover:text-white" data-testid={`sb-file-clear-${k}`}>clear</button>}
                    </div>
                    <input type="file" accept={accept} data-testid={`sb-file-${k}`} onChange={(e) => setFiles({ ...files, [k]: e.target.files?.[0] || null })}
                        className="mt-2 block w-full text-xs text-white/70 file:mr-3 file:rounded-full file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-xs file:text-white hover:file:bg-white/20" />
                    {!files[k] && (
                        <input value={ids[k]} onChange={(e) => setIds({ ...ids, [k]: e.target.value })} data-testid={`sb-id-${k}`} placeholder={`…or paste an existing ${k} id / Data Lake item id`}
                            className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs font-mono" />
                    )}
                </div>
            ))}
        </div>
    );
}

// Client → protocol → budget / CTA (filtered to the same study, "show all" toggle).
function ExistingPicker({ qaMode, clientId, ids, setIds }) {
    const [docs, setDocs] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        setDocs(null); setShowAll(false);
        if (!clientId) return;
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/admin/study-assets/study-documents`, { params: { client_id: clientId, protocol_id: ids.protocol || undefined, include_test_protocols: qaMode } });
                if (alive) setDocs(data);
            } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [clientId, ids.protocol, qaMode]);

    const protocols = docs?.protocols || [];
    const annotated = Boolean(docs?.protocol && ids.protocol);
    const budgetsAll = docs?.budgets || [];
    const ctasAll = docs?.ctas || [];
    const budgets = annotated && !showAll ? budgetsAll.filter((b) => b.matches_study) : budgetsAll;
    const ctas = annotated && !showAll ? ctasAll.filter((c) => c.matches_study) : ctasAll;
    const protocolLabel = (p) => `${p.sponsor ? `${p.sponsor} — ` : ""}${p.protocol_number || "(unknown)"} v${p.version || "?"}${p.title ? ` · ${p.title.slice(0, 40)}` : ""}${p.is_test_protocol ? " · test" : ""}`;
    const assetLabel = (d) => [d.protocol_number || d.sponsor || d.filename || d.id?.slice(0, 8), d.version ? `v${d.version}` : null, d.filename && (d.protocol_number || d.sponsor) ? d.filename : null, d.status && d.status !== "ready" ? d.status : null, annotated && showAll && !d.matches_study ? "other study" : null].filter(Boolean).join(" · ");
    const note = (kind, all) => {
        if (!annotated) return null;
        const n = all.filter((d) => d.matches_study).length;
        return n === 0
            ? <span className="text-amber-200/80" data-testid={`sb-${kind}-nomatch`}>No {kind} found for {docs.protocol.protocol_number || "this study"} — {all.length} other {kind} for this client.</span>
            : <span data-testid={`sb-${kind}-match`}>{n} of {all.length} {kind} belong to {docs.protocol.protocol_number || "this study"}.</span>;
    };
    return (
        <div className="space-y-3" data-testid="sb-existing-fields">
            <Select label={`2 · Protocol${loading ? " (loading…)" : docs ? ` (${protocols.length})` : ""}`} value={ids.protocol} onChange={(v) => setIds({ protocol: v, budget: "", cta: "" })} disabled={!clientId || loading}
                placeholder={!clientId ? "(pick a client first)" : protocols.length ? "(select…)" : "(no protocols for this client)"} options={protocols.map((p) => ({ value: p.id, label: protocolLabel(p) }))} testid="sb-protocol-select" />
            <Select label={`3 · Budget${annotated ? ` (${budgets.length})` : ""}`} value={ids.budget} onChange={(v) => setIds({ ...ids, budget: v })} disabled={!ids.protocol}
                placeholder={!ids.protocol ? "(pick a protocol first)" : budgets.length ? "(select…)" : "(no budget for this study)"} options={budgets.map((b) => ({ value: b.id, label: assetLabel(b) }))} testid="sb-budget-select" note={note("budgets", budgetsAll)} />
            <Select label={`CTA (optional)${annotated ? ` (${ctas.length})` : ""}`} value={ids.cta} onChange={(v) => setIds({ ...ids, cta: v })} disabled={!ids.protocol}
                placeholder={!ids.protocol ? "(pick a protocol first)" : "(none)"} options={ctas.map((c) => ({ value: c.id, label: assetLabel(c) }))} testid="sb-cta-select" note={note("CTAs", ctasAll)} />
            {annotated && (budgetsAll.length > budgets.length || ctasAll.length > ctas.length || showAll) && (
                <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer" data-testid="sb-show-all-label">
                    <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="sb-show-all" className="accent-[#7C3AED]" />
                    Show all of this client's budgets and CTAs (other studies too)
                </label>
            )}
        </div>
    );
}

function Select({ label, value, onChange, options, testid, disabled = false, placeholder = "(select…)", note }) {
    return (
        <label className="block">
            <div className="text-xs text-white/50 mb-1">{label}</div>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                <option value="">{placeholder}</option>
                {options.map((o) => <option key={o.value || o.label} value={o.value}>{o.label}</option>)}
            </select>
            {note && <div className="text-[11px] text-white/45 mt-1">{note}</div>}
        </label>
    );
}
